import {
  default as StyledTableCell,
  default as TableCell
} from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import {
  TABLE_BODY_CELL_FONT_SIZE,
  TABLE_HEADER_CELL_FONT_SIZE
} from "@constants/table";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  useTheme
} from "@mui/material";
import {
  useClientModels,
  useClientModelsExecutionStatus,
  useClients,
  useDeleteClientModel
} from "@services/api";
import AdminPanelNavBar from "@views/ViewAdminPanel/components/AdminPanelNavBar";
import ClientSelector from "@views/ViewAdminPanel/components/ClientSelector";

import ConfirmationDialog from "@components/ConfirmationDialog";
import CustomSnackBar from "@components/CustomSnackBar";
import { TableRowsSkeleton } from "@components/Table/TableRowsSkeleton";
import {
  ROUTING_ADMIN_CLIENT_MODEL,
  ROUTING_ADMIN_NEW_CLIENT_MODEL
} from "@constants/adminPanelRouting";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState,
  setSuccessAlert,
  setWarningAlert
} from "@constants/alerts";
import {
  BUTTON_PRIMARY_ICON_FONT_SIZE,
  BUTTON_SECONDARY_ICON_FONT_SIZE
} from "@constants/button";
import ROUTES from "@constants/routing";
import AddIcon from "@mui/icons-material/Add";
import ScienceIcon from "@mui/icons-material/Science";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ClientModel, ClientShort } from "@services/api/types";
import { useQueryClient } from "@tanstack/react-query";
import { defaultLabelDisplayedRows } from "@utils/table";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  columns,
  getExecutionStatusColor,
  getExecutionStatusIcon
} from "./ClientModelTableTypes";

const ViewClientModels = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [selectedClient, setSelectedClient] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<ClientModel | null>(null);
  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });

  const mutationDeleteClientModel = useDeleteClientModel();

  const queryClient = useQueryClient();

  const { data: clients, isFetching: isLoadingClients } = useClients();

  const {
    data: models,
    isFetching: isLoadingModels,
    isError: isErrorModels
  } = useClientModels(selectedClient);

  const { data: modelsExecutionStatus, isFetching: isLoadingExecutionStatus } =
    useClientModelsExecutionStatus(
      models?.map((model) => model.id.toString()) || []
    );

  const onClientSelectChange = (clientId: string) => {
    setSelectedClient(clientId.toString());
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const onNewModelClick = () => {
    navigate(
      `${ROUTES.ADMIN_PANEL}${ROUTING_ADMIN_NEW_CLIENT_MODEL.replace(":id", selectedClient)}`
    );
  };

  const onViewModelClick = (modelId: string) => {
    navigate(
      `${ROUTES.ADMIN_PANEL}${ROUTING_ADMIN_CLIENT_MODEL.replace(":id", selectedClient).replace(":modelId", modelId)}`
    );
  };

  const handleDeleteClientModel = () => {
    if (!modelToDelete) {
      return;
    }
    mutationDeleteClientModel.mutate(
      {
        model_id: modelToDelete.id.toString()
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["client", selectedClient, "models"]
          });
          queryClient.refetchQueries({
            queryKey: ["client", selectedClient, "models"]
          });
          setOpenConfirmationDialog(false);
          setSnackbarAlert(
            setSuccessAlert(
              t("admin.clients.delete_model_success", {
                name: modelToDelete?.name || ""
              })
            )
          );
          setModelToDelete(null);
        },
        onError: () => {
          setOpenConfirmationDialog(false);
          setSnackbarAlert(
            setErrorAlert(
              t("admin.clients.delete_model_error", {
                name: modelToDelete?.name || ""
              })
            )
          );
          setModelToDelete(null);
        }
      }
    );
  };

  // Read clientId from navigation state
  useEffect(() => {
    const state = location.state as { clientId?: string } | null;
    if (state?.clientId && state.clientId.length > 0) {
      setSelectedClient(state.clientId);
    }
  }, [location.state]);

  const isLoading = useMemo(() => {
    return isLoadingModels || isLoadingExecutionStatus;
  }, [isLoadingModels, isLoadingExecutionStatus]);

  const modelStatusMapping = useMemo(() => {
    return modelsExecutionStatus?.reduce(
      (acc, model) => {
        acc[model.model_id] = model.status;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [modelsExecutionStatus]);

  const clientMapping = useMemo(() => {
    return clients?.reduce(
      (acc, client) => {
        acc[client.id.toString()] = client;
        return acc;
      },
      {} as Record<string, ClientShort>
    );
  }, [clients]);

  useEffect(() => {
    if (
      !isLoadingModels &&
      !isErrorModels &&
      clientMapping &&
      selectedClient.length > 0 &&
      models &&
      models.length === 0
    ) {
      setSnackbarAlert(
        setWarningAlert(
          t("clientExternalEffects.noModelsFound", {
            name: clientMapping[selectedClient].name
          })
        )
      );
    }
  }, [isLoadingClients, isErrorModels, models, selectedClient, clientMapping]);

  return (
    <Box className="w-full p-4">
      <AdminPanelNavBar activeTab="models" />
      <Box className="flex justify-center w-full p-2 md:p-8 mt-8 md:mt-16">
        <Paper className="w-full">
          <Box className="flex justify-center w-full md:w-1/5 p-8">
            <ClientSelector
              selectedClient={selectedClient}
              setSelectedClient={onClientSelectChange}
            />
          </Box>
          <Box className="pl-8">
            <Tooltip title={t("common.newModel")}>
              <IconButton
                color="info"
                onClick={onNewModelClick}
                disabled={isLoading || selectedClient.length === 0}
                style={{ backgroundColor: "white" }}
              >
                <ScienceIcon sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }} />
                <sup>
                  <AddIcon sx={{ fontSize: BUTTON_SECONDARY_ICON_FONT_SIZE }} />
                </sup>
              </IconButton>
            </Tooltip>
          </Box>
          <TableContainer className="w-full p-8">
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <StyledTableCell
                    key="collapse-column"
                    style={{ width: 5, borderRight: "1px solid #caccce" }}
                  ></StyledTableCell>
                  {columns.map((column, index) => (
                    <StyledTableCell
                      key={column.id}
                      align={column.align}
                      style={{
                        minWidth: column.minWidth,
                        borderRight:
                          index === columns.length - 1
                            ? "none"
                            : "1px solid #caccce",
                        fontSize: TABLE_HEADER_CELL_FONT_SIZE
                      }}
                    >
                      {t(`clientModel.${column.label}`)}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRowsSkeleton
                    id={`client-model-${selectedClient}-table-rows-skeleton`}
                    numberOfRows={rowsPerPage}
                    columns={columns.length + 1}
                  />
                ) : (
                  (models || [])
                    .sort((a, b) => b.id - a.id)
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <StyledTableRow
                        sx={{ "& > *": { borderBottom: "unset" } }}
                        key={row.id}
                      >
                        <TableCell component="th" scope="row" align="center">
                          <Box className="flex justify-center gap-2">
                            <Tooltip title={t("common.view")}>
                              <IconButton
                                color="info"
                                onClick={() => {
                                  onViewModelClick(row.id.toString());
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("common.delete")}>
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setOpenConfirmationDialog(true);
                                  setModelToDelete(row);
                                }}
                                disabled={
                                  isLoadingExecutionStatus ||
                                  !modelStatusMapping ||
                                  !(row.id.toString() in modelStatusMapping) ||
                                  !["SUCCEEDED", "FAILED"].includes(
                                    modelStatusMapping[row.id.toString()]
                                  )
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          {row.year}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          {t(
                            `clientModel.aggregation_types.${row.aggregation_type}`
                          )}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          {modelStatusMapping &&
                            row.id.toString() in modelStatusMapping && (
                              <Box className="flex justify-center gap-2">
                                <Box
                                  className="flex justify-center"
                                  sx={{
                                    color: getExecutionStatusColor(
                                      modelStatusMapping[row.id.toString()],
                                      theme
                                    )
                                  }}
                                >
                                  {getExecutionStatusIcon(
                                    modelStatusMapping[row.id.toString()]
                                  )}
                                </Box>
                                {t(
                                  `clientModel.status.${modelStatusMapping[row.id.toString()]}`
                                )}
                              </Box>
                            )}
                        </TableCell>
                      </StyledTableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage={t("table.rows_per_page")}
            labelDisplayedRows={defaultLabelDisplayedRows}
            component="div"
            count={(models || []).length ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={() => setOpenConfirmationDialog(false)}
          onConfirm={handleDeleteClientModel}
          title={t("admin.clients.delete_model", {
            name: modelToDelete?.name || ""
          })}
        />
        <CustomSnackBar
          id={snackbarAlert.message}
          key={snackbarAlert.message}
          open={snackbarAlert.open}
          message={snackbarAlert.message}
          handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
          autoHideDuration={snackbarAlert.autoHideDuration}
          color={snackbarAlert.severity}
        />
      </Box>
    </Box>
  );
};

export default ViewClientModels;
